import { useCallback, useMemo } from 'react';
import type { AssetVerified } from '@terra/ui-design-system';
import type { AssetsSearchGroupBy } from '@terra/shared-contracts-assets';
import type { AssetType } from '../../../store/tab/assetsTabScopedFilterStore';
import {
  ASSETS_TABLE_GROUP_BY,
  useTabScopedAssetsTableView,
} from '../../../store/tab/assetsTableViewTabScopedStore';
import {
  getAssetsTableGroupByOptions,
  resolveAssetsTableKind,
  resolveAssetsTableOrderBy,
  resolveEffectiveAssetsGroupBy,
} from './assets-table.model';

export function useAssetsTableView({
  typeFilter,
  verified,
}: {
  typeFilter: AssetType;
  verified: AssetVerified;
}) {
  const view = useTabScopedAssetsTableView();
  const { setGroupBy: setStoredGroupBy } = view;
  const kind = resolveAssetsTableKind({ typeFilter, verified });
  const groupBy = resolveEffectiveAssetsGroupBy({
    groupBy: view.groupBy,
    kind,
  });
  const orderBy = useMemo(
    () => resolveAssetsTableOrderBy({ sort: view.sort, kind }),
    [kind, view.sort],
  );
  const groupByOptions = useMemo(
    () => getAssetsTableGroupByOptions(kind),
    [kind],
  );
  const collapsedGroupKeys = useMemo(
    () => new Set(view.collapsedGroupKeys),
    [view.collapsedGroupKeys],
  );
  const setGroupBy = useCallback(
    (nextGroupBy: AssetsSearchGroupBy | null) =>
      setStoredGroupBy(nextGroupBy ?? ASSETS_TABLE_GROUP_BY.NONE),
    [setStoredGroupBy],
  );

  return {
    kind,
    groupBy,
    groupByOptions,
    orderBy,
    sort: view.sort,
    setSort: view.setSort,
    setGroupBy,
    collapsedGroupKeys,
    setCollapsedGroupKeys: view.setCollapsedGroupKeys,
    toggleGroupCollapsed: view.toggleGroupCollapsed,
  };
}

export type AssetsTableView = ReturnType<typeof useAssetsTableView>;
